import { useState } from "react";
import { api, unwrap } from "../api";
import {
  Button,
  DeleteButton,
  Empty,
  ErrorNote,
  Field,
  Loading,
  Panel,
  Table,
  TableSearch,
  Td,
  Th,
  input,
  useTable,
} from "../ui";
import { useDeleteRecord } from "../useDeleteRecord";
import { useResource } from "../useResource";

const KINDS = ["labor", "material", "subcontract", "equipment", "overhead", "other"] as const;

export function CostCodes({ slug, role }: { slug: string; role: string }) {
  const del = useDeleteRecord(slug);
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [kind, setKind] = useState<(typeof KINDS)[number]>("labor");
  const [parentId, setParentId] = useState("");

  const r = useResource({
    key: ["cost-codes", slug],
    list: async () => unwrap(await api.api.c[":slug"]["cost-codes"].$get({ param: { slug } })),
    create: async (body: {
      code: string;
      name: string;
      kind: (typeof KINDS)[number];
      parentId?: string | null;
    }) => unwrap(await api.api.c[":slug"]["cost-codes"].$post({ param: { slug }, json: body })),
  });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    try {
      await r.create({
        code: code.trim(),
        name: name.trim(),
        kind,
        parentId: parentId || null,
      });
      setCode("");
      setName("");
      setParentId("");
      setOpen(false);
    } catch {
      /* surfaced through r.formError */
    }
  }

  const all = r.query.data?.costCodes ?? [];
  const depthOf = (id: string | null): number => {
    let d = 0;
    let cur = id;
    while (cur) {
      const parent = all.find((x) => x.id === cur);
      if (!parent) break;
      d++;
      cur = parent.parentId;
    }
    return d;
  };

  const costCodesTable = useTable(all, {
    filter: (c, q) => `${c.code} ${c.name} ${c.kind}`.toLowerCase().includes(q.toLowerCase()),
    sorters: {
      code: (c) => c.code.toLowerCase(),
      name: (c) => c.name.toLowerCase(),
      kind: (c) => c.kind,
    },
  });

  return (
    <Panel
      title="Cost codes"
      action={
        <Button
          onClick={() => {
            setOpen(!open);
            r.clearErrors();
          }}
        >
          {open ? "Cancel" : "New cost code"}
        </Button>
      }
    >
      {open && (
        <form
          onSubmit={submit}
          className="mb-6 rounded-xl border border-neutral-200 bg-white p-5 shadow-sm"
        >
          {r.formError && <ErrorNote>{r.formError}</ErrorNote>}
          <div className="grid gap-3 sm:grid-cols-4">
            <Field label="Code" error={r.fieldErrors.code}>
              <input
                className={input}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            </Field>
            <Field label="Name" error={r.fieldErrors.name}>
              <input
                className={input}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Field>
            <Field label="Kind">
              <select
                className={input}
                value={kind}
                onChange={(e) => setKind(e.target.value as typeof kind)}
              >
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Parent" hint="Optional">
              {/* Only existing codes are offered, so a cycle cannot be built
                  through the UI at all. */}
              <select
                className={input}
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
              >
                <option value="">— none —</option>
                {all.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code} {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="mt-3">
            <Button type="submit" disabled={r.saving}>
              {r.saving ? "Saving…" : "Add cost code"}
            </Button>
          </div>
        </form>
      )}

      {del.error && <ErrorNote>{del.error}</ErrorNote>}

      {r.query.isPending ? (
        <Loading />
      ) : r.query.isError ? (
        <ErrorNote>{(r.query.error as Error).message}</ErrorNote>
      ) : all.length === 0 ? (
        <Empty title="No cost codes yet">
          Without these, expenses can't be classified and budget-vs-actual won't work.
        </Empty>
      ) : (
        <>
          <TableSearch
            value={costCodesTable.query}
            onChange={costCodesTable.setQuery}
            placeholder="Filter cost codes…"
          />
          <Table
            head={
              <>
                <Th
                  sortKey="code"
                  active={costCodesTable.sortKey === "code"}
                  dir={costCodesTable.dir}
                  onSort={costCodesTable.toggleSort}
                >
                  Code
                </Th>
                <Th
                  sortKey="name"
                  active={costCodesTable.sortKey === "name"}
                  dir={costCodesTable.dir}
                  onSort={costCodesTable.toggleSort}
                >
                  Name
                </Th>
                <Th
                  sortKey="kind"
                  active={costCodesTable.sortKey === "kind"}
                  dir={costCodesTable.dir}
                  onSort={costCodesTable.toggleSort}
                >
                  Kind
                </Th>
                {role === "owner" && <Th />}
              </>
            }
          >
            {costCodesTable.rows.map((c) => (
              <tr
                key={c.id}
                className="border-b border-neutral-100 even:bg-neutral-50 transition-colors hover:bg-neutral-100"
              >
                <Td mono muted>
                  {c.code}
                </Td>
                <Td>
                  <span style={{ paddingLeft: `${depthOf(c.parentId) * 16}px` }}>{c.name}</span>
                </Td>
                <Td muted>{c.kind}</Td>
                {role === "owner" && (
                  <Td right>
                    <DeleteButton
                      busy={del.deleting}
                      onConfirm={() => del.remove("cost_code", c.id)}
                    />
                  </Td>
                )}
              </tr>
            ))}
          </Table>
        </>
      )}
    </Panel>
  );
}
